import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiStar, FiInstagram, FiScissors } from 'react-icons/fi';
import { staffAPI } from '../api';
import SEO from '../components/seo/SEO';
import { breadcrumb } from '../components/seo/jsonLd';

const FALLBACK = [
  { id:1, name:'Amara Nwosu', role:'Senior Stylist', bio:'10+ years crafting iconic looks with passion and precision.', specialties:['Balayage','Precision Cuts','Color Correction'], rating:4.9, reviews:128, image:null },
  { id:2, name:'Sofia Mendes', role:'Bridal Artist', bio:'Specialist in bridal and editorial makeup with a flawless touch.', specialties:['Bridal Makeup','Airbrush','Contouring'], rating:4.8, reviews:96, image:null },
  { id:3, name:'Leila Rashid', role:'Color Specialist', bio:'Transformative color expert with an eye for tone and texture.', specialties:['Highlights','Balayage','Toning'], rating:4.9, reviews:110, image:null },
  { id:4, name:'Yuki Tanaka', role:'Nail Technician', bio:'Meticulous nail artist turning nails into miniature masterpieces.', specialties:['Gel Nails','Nail Art','Manicure'], rating:4.7, reviews:84, image:null },
  { id:5, name:'Priya Sharma', role:'Skin Therapist', bio:'Certified esthetician dedicated to glowing, healthy skin.', specialties:['Facials','Chemical Peels','Dermaplaning'], rating:4.8, reviews:72, image:null },
  { id:6, name:'Chloe Laurent', role:'Hair Stylist', bio:'Trend-forward stylist bringing Parisian chic to every cut.', specialties:['Blowouts','Updos','Keratin'], rating:4.7, reviews:65, image:null },
];

export default function Staff() {
  const { data: staff = FALLBACK } = useQuery({
    queryKey: ['staff'],
    queryFn: () => staffAPI.getAll().then(r => r.data.data || r.data),
    placeholderData: FALLBACK,
  });

  return (
    <>
      <SEO
        title="Meet the Salon Vora Team — Senior Stylists & Beauty Artists"
        description="Meet the senior stylists, colourists, makeup artists, nail technicians and skin therapists behind Salon Vora. Pick your favourite and book online."
        path="/our-team"
        jsonLd={breadcrumb([
          { name: 'Home', url: '/' },
          { name: 'Our Team', url: '/our-team' },
        ])}
      />
    <div className="min-h-screen bg-salon-cream">
      {/* Hero */}
      <section className="relative bg-hero-gradient py-32 text-center overflow-hidden">
        <div className="absolute inset-0 opacity-20"
          style={{ backgroundImage:'radial-gradient(circle at 30% 50%, #C9963A 0%, transparent 60%)' }} />
        <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-salon-primary/30 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-40 -left-40 w-[400px] h-[400px] rounded-full bg-salon-secondary/15 blur-3xl pointer-events-none" />
        <div className="container mx-auto px-4 relative z-10">
          <motion.span initial={{ opacity:0, y:20 }} animate={{ opacity:1, y:0 }}
            className="section-tag mb-4 inline-block">Meet the Team</motion.span>
          <motion.h1 initial={{ opacity:0, y:30 }} animate={{ opacity:1, y:0 }} transition={{ delay:.1 }}
            className="font-display text-4xl md:text-6xl text-white mb-6">
            Talented Artists,<br /><span className="text-salon-secondary">Passionate Souls</span>
          </motion.h1>
          <motion.p initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ delay:.2 }}
            className="text-white/70 text-lg max-w-2xl mx-auto">
            Every member of our team is hand-picked for their skill, creativity and commitment to making you feel extraordinary.
          </motion.p>
        </div>
      </section>

      {/* Team Grid */}
      <section className="py-20 container mx-auto px-4">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {staff.map((member, i) => (
            <motion.div key={member.id}
              initial={{ opacity:0, y:30 }} animate={{ opacity:1, y:0 }} transition={{ delay: i*0.08 }}
              className="card group text-center overflow-hidden">
              {/* Avatar */}
              <div className="relative">
                <div className="h-64 bg-gradient-to-br from-salon-primary/20 to-salon-secondary/10 flex items-center justify-center">
                  {member.image
                    ? <img src={member.image} alt={member.name} className="w-full h-full object-cover" />
                    : <div className="w-28 h-28 rounded-full bg-salon-primary/20 flex items-center justify-center text-5xl font-display text-salon-primary">
                        {member.name[0]}
                      </div>}
                </div>
                <div className="absolute top-4 right-4 bg-white rounded-full px-2.5 py-1 flex items-center gap-1 shadow text-sm font-semibold text-salon-dark">
                  <FiStar className="text-salon-secondary fill-salon-secondary" />{member.rating}
                </div>
              </div>

              <div className="p-6">
                <h3 className="font-display text-2xl text-salon-dark group-hover:text-salon-primary transition-colors">{member.name}</h3>
                <p className="text-salon-primary font-medium text-sm mb-3">{member.role}</p>
                <p className="text-salon-muted text-sm mb-4">{member.bio}</p>

                {/* Specialties */}
                <div className="flex flex-wrap gap-2 justify-center mb-5">
                  {(member.specialties || []).map(sp => (
                    <span key={sp} className="flex items-center gap-1 text-xs bg-salon-cream-dark text-salon-primary px-2.5 py-1 rounded-full">
                      <FiScissors className="text-[10px]" />{sp}
                    </span>
                  ))}
                </div>

                <p className="text-xs text-salon-muted mb-4">{member.reviews} happy clients</p>
                <Link to={`/book?staff=${member.id}`}
                  className="btn-primary w-full text-center">Book with {member.name.split(' ')[0]}</Link>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Join Team CTA */}
      <section className="bg-gradient-to-r from-salon-dark to-salon-primary/90 py-20 text-center">
        <div className="container mx-auto px-4">
          <h2 className="font-display text-3xl md:text-4xl text-white mb-4">Join Our Team</h2>
          <p className="text-white/70 mb-8 max-w-xl mx-auto">
            Are you a talented beauty professional passionate about your craft? We'd love to meet you.
          </p>
          <Link to="/careers" className="btn-gold">Apply Now</Link>
        </div>
      </section>
    </div>
    </>
  );
}
